import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, ArrowRight, BookOpen } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getAllInsightPages, allClusters } from "@/data/contentClusters";

const categories = [
  { id: "all", label: "All Articles" },
  { id: "geo", label: "GEO & AI Search" },
  { id: "ai-ads", label: "AI Ads" },
  { id: "ai-content", label: "AI Content" },
  { id: "ai-agents", label: "AI Agents" },
];

export default function Insights() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const allInsights = getAllInsightPages();

  const filteredInsights = useMemo(() => {
    return allInsights.filter((insight) => {
      const matchesSearch = 
        insight.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        insight.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = 
        activeCategory === "all" || insight.pillarSlugs.includes(activeCategory);
      
      return matchesSearch && matchesCategory;
    });
  }, [allInsights, searchQuery, activeCategory]);

  const featuredInsights = allInsights.slice(0, 3);

  const getPillarColor = (pillarSlug: string) => {
    const colors: Record<string, string> = {
      "geo": "bg-primary/10 text-primary",
      "ai-ads": "bg-orange-500/10 text-orange-500",
      "ai-content": "bg-green-500/10 text-green-500",
      "ai-agents": "bg-purple-500/10 text-purple-500",
    };
    return colors[pillarSlug] || "bg-muted text-muted-foreground";
  };

  const getPillarName = (pillarSlug: string) => {
    const cluster = allClusters.find(c => c.pillar.slug === pillarSlug);
    return cluster?.pillar.title || pillarSlug;
  };

  return (
    <Layout>
      <SEO 
        title="Insights | AI Marketing Knowledge Hub"
        description="Expert insights on GEO, AI search, content marketing, and automation. Learn how AI is transforming B2B marketing."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs 
            items={[
              { label: "Home", href: "/" },
              { label: "Insights" }
            ]}
            className="mb-8"
          />
          <AnimatedSection className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              Knowledge Hub
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              AI Marketing <span className="text-gradient">Insights</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Expert analysis on GEO, AI-powered search, content strategy, and marketing automation. 
              Stay ahead of the curve in B2B marketing.
            </p>

            {/* Search */}
            <div className="relative max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base rounded-full"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Section */}
      {!searchQuery && activeCategory === "all" && (
        <section className="py-16 border-b border-border">
          <div className="container-wide">
            <AnimatedSection>
              <h2 className="text-2xl font-bold mb-8">Featured Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {featuredInsights.map((insight, index) => (
                  <motion.div
                    key={insight.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link 
                      to={`/insights/${insight.slug}`}
                      className="group block p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                    >
                      <div className="flex items-center gap-2 mb-4">
                        {insight.pillarSlugs.map(slug => (
                          <Badge key={slug} className={getPillarColor(slug)}>
                            {getPillarName(slug)}
                          </Badge>
                        ))}
                      </div>
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {insight.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {insight.description}
                      </p>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="w-4 h-4 mr-1" />
                        {insight.readingTime}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Category Filters */}
      <section className="py-8 border-b border-border sticky top-16 md:top-20 bg-background/80 backdrop-blur-lg z-30">
        <div className="container-wide">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory(category.id)}
                className="rounded-full"
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container-wide">
          <AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredInsights.map((insight, index) => (
                <motion.div
                  key={insight.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link 
                    to={`/insights/${insight.slug}`}
                    className="group block p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant="outline" className="text-xs">
                        {insight.category}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                      {insight.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {insight.description}
                    </p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {insight.readingTime}
                      </div>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {filteredInsights.length === 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground">No articles found matching your search.</p>
              </div>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 border-t border-border bg-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Stay Ahead of AI Marketing</h2>
            <p className="text-muted-foreground mb-8">
              Get weekly insights on GEO, AI search, and B2B marketing automation delivered to your inbox.
            </p>
            <Button 
              size="lg" 
              className="rounded-full"
              asChild
            >
              <a href="https://thecloudseo.beehiiv.com/" target="_blank" rel="noopener noreferrer">
                Subscribe to Newsletter
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
